// src/runtime/SKURuntime/types.ts
// ============================================
// SKU Runtime 类型定义（Sprint 4 Phase A-2）
//
// 产品知识库全部存在 Obsidian Vault 的 SKU/*.md 中
// 每个 SKU 一个 Markdown 文件，包含 frontmatter + 正文章节
// Hermes 通过本 Runtime 查询产品信息，直接引用销售话术
// ============================================

// ============================================
// SKU 状态与分类
// ============================================

/** SKU 上架状态 */
export type SKUStatus = 'active' | 'inactive' | 'deprecated';

/** SKU 类目 */
export type SKUCategory = '咨询' | '培训' | '开发' | '陪跑' | '其他';

// ============================================
// 销售渠道与业绩追踪（Sprint 4 P0-4 新增）
// ============================================

/** 销售渠道类型 */
export type SalesChannel =
  | 'wechat'        // 微信私域（朋友圈/社群/1v1）
  | 'xiaohongshu'   // 小红书引流
  | 'douyin'        // 抖音直播间/短视频
  | 'website'       // 官网表单
  | 'referral'      // 老客户转介绍
  | 'sales'         // 销售主动开发
  | 'partner';      // 渠道伙伴

/** 单条销售记录（写入 SKU 销售台账） */
export interface SalesRecord {
  /** 记录 ID（自动生成） */
  id: string;
  /** 关联的 SKU ID */
  skuId: string;
  /** 关联的客户 ID（来自 CRMRuntime，可为空表示匿名成单） */
  customerId?: string;
  /** 客户公司名（冗余字段，便于脱机分析） */
  customerCompany?: string;
  /** 销售渠道 */
  channel: SalesChannel;
  /** 成交数量 */
  quantity: number;
  /** 成交单价（用于计算 revenue/profit） */
  unitPrice: number;
  /** 总收入（quantity * unitPrice，自动计算） */
  revenue: number;
  /** 总利润（revenue - cost，由 cost 字段计算得出，无 cost 时等于 revenue） */
  profit: number;
  /** 总成本（用于利润统计，可选） */
  cost?: number;
  /** 成交日期 YYYY-MM-DD */
  dealDate: string;
  /** 负责销售（可关联 AgentRuntime.salesAgent） */
  salesPerson?: string;
  /** 备注 */
  note?: string;
  /** 关联的内容 ID（来自 ContentFactory，记录是哪篇内容带来的成单） */
  contentId?: string;
  /** 关联的 SOP 执行记录 ID（来自 SOPRuntime，记录是按哪条 SOP 跟进的） */
  sopExecutionId?: string;
}

/** SKU 业绩汇总（按 SKU 聚合） */
export interface SKUPerformance {
  /** SKU ID */
  skuId: string;
  /** SKU 名称（冗余） */
  skuName: string;
  /** 总销售量（笔数 = salesRecords.length） */
  totalDeals: number;
  /** 总成交数量（quantity 之和） */
  totalQuantity: number;
  /** 总收入（revenue 之和） */
  totalRevenue: number;
  /** 总利润（profit 之和） */
  totalProfit: number;
  /** 平均客单价（revenue / totalDeals） */
  avgDealSize: number;
  /** 最近一次成交日期 YYYY-MM-DD */
  lastDealDate?: string;
  /** 按渠道统计销售量 */
  byChannel: Record<SalesChannel, number>;
  /** 按渠道统计收入 */
  revenueByChannel: Record<SalesChannel, number>;
}

// ============================================
// SKU 档案模型
// ============================================

/** SKU 档案（完整模型，对应一个 .md 文件） */
export interface SKUProfile {
  /** SKU ID（文件名去掉 .md） */
  id: string;
  /** SKU 编号（frontmatter 中的 sku 字段，如 SKU-001） */
  sku: string;
  /** 产品名称 */
  name: string;
  /** 价格（数值，单位由 unit 字段说明） */
  price: number;
  /** 价格单位（如 元/项目、元/天） */
  unit: string;
  /** 产品类目 */
  category: SKUCategory;
  /** 上架状态 */
  status: SKUStatus;
  /** 适合的客户/场景标签 */
  suitableFor: string[];
  /** 产品介绍（正文 ## 产品介绍 章节） */
  introduction: string;
  /** 核心卖点（正文 ## 核心卖点 章节） */
  sellingPoints: string;
  /** 常见问题（正文 ## FAQ 章节） */
  faq: string;
  /** 成功案例（正文 ## 成功案例 章节） */
  cases: string;
  /** 销售 Prompt（正文 ## 销售 Prompt 章节，Hermes 直接引用） */
  salesPrompt: string;
  /** 交付流程（正文 ## 交付流程 章节） */
  deliveryProcess: string;
  /**
   * 销售台账（Sprint 4 P0-4 新增）
   * 该 SKU 的所有成交记录，用于业绩统计与"哪个 SKU 卖得最好"分析
   * 默认为空数组，由 recordSale 命令追加
   */
  salesRecords: SalesRecord[];
  /**
   * 主推渠道（Sprint 4 P0-4 新增）
   * Hermes 据此判断该 SKU 应通过哪个渠道推广
   * 例如 ['xiaohongshu', 'wechat'] 表示该 SKU 在小红书引流、微信成单
   */
  primaryChannels: SalesChannel[];
  /** 文件绝对路径（浏览器环境下为虚拟路径） */
  filePath: string;
}

// ============================================
// 查询与命令枚举
// ============================================

export enum SKUQuery {
  /** 列出所有 SKU */
  List = 'list',
  /** 获取单个 SKU */
  Get = 'get',
  /** 搜索 SKU */
  Search = 'search',
  /** 按类目筛选 */
  FindByCategory = 'find_by_category',
  /** 按适合场景筛选 */
  FindSuitableFor = 'find_suitable_for',
  /** 获取销售 Prompt（Hermes 直接引用） */
  GetSalesPrompt = 'get_sales_prompt',
  /** 统计概览 */
  Stats = 'stats',
  /**
   * 卖得最好的 SKU（Sprint 4 P0-4 新增）
   * 返回 SKUPerformance[]，按 totalRevenue 降序
   * params: { top?: number; channel?: SalesChannel; startDate?: string; endDate?: string }
   */
  TopPerformers = 'top_performers',
  /** 获取某个 SKU 的业绩汇总（Sprint 4 P0-4 新增） */
  GetPerformance = 'get_performance',
  /** 获取某个 SKU 的销售台账（Sprint 4 P0-4 新增） */
  ListSalesRecords = 'list_sales_records',
}

export enum SKUCommand {
  /** 创建 SKU 档案 */
  Create = 'create',
  /** 更新 SKU 档案 */
  Update = 'update',
  /** 更新价格 */
  UpdatePrice = 'update_price',
  /** 更新销售 Prompt */
  UpdateSalesPrompt = 'update_sales_prompt',
  /**
   * 记录一笔销售（Sprint 4 P0-4 新增）
   * params: SalesRecord（不含 id 与 revenue/profit，由 Service 自动计算）
   * 追加到 SKUProfile.salesRecords，并触发业绩重算
   */
  RecordSale = 'record_sale',
  /** 设置主推渠道（Sprint 4 P0-4 新增） */
  SetPrimaryChannels = 'set_primary_channels',
  /**
   * V1.5 P1-5：Offer 产品设计
   * params: { inquiry: string; customerName?: string }
   * 把客户咨询转化为可销售产品包（需求/方案/报价/毛利），落盘 XEAOS/SKU/Offers/{date}.md
   */
  DesignOffer = 'design_offer',
}

// ============================================
// V1.5 P1-5：Offer 产品设计（需求 → 产品包 → 报价 → 毛利）
// ============================================

/** Offer 产品包条目 */
export interface OfferPackageItem {
  /** 条目名（如"酒店朋友圈内容"） */
  name: string;
  /** 数量 */
  count: number;
  /** 单位（条/套/个） */
  unit: string;
}

/** Offer 产品设计方案（design_offer 命令输出） */
export interface OfferDesign {
  /** 方案 ID（offer-{timestamp}） */
  id: string;
  /** 生成日期 YYYY-MM-DD */
  date: string;
  /** 客户原始咨询文本 */
  inquiry: string;
  /** 客户名称（可选） */
  customerName?: string;
  /** 结构化客户需求（从咨询文本解析） */
  need: string;
  /** 解决方案概述 */
  solution: string;
  /** 命中的 SKU ID（无命中时为空） */
  matchedSkuId?: string;
  /** 命中的 SKU 名称 */
  matchedSkuName?: string;
  /** 产品包内容 */
  packageItems: OfferPackageItem[];
  /** 报价（元） */
  offerPrice: number;
  /** 交付成本估算（元） */
  deliveryCost: number;
  /** 预计毛利（元）= offerPrice - deliveryCost */
  grossProfit: number;
  /** 毛利率（0-1） */
  marginRate: number;
  /** 落盘路径（Vault Offers 目录） */
  filePath?: string;
}

/** SKU 统计概览 */
export interface SKUStats {
  /** SKU 总数 */
  total: number;
  /** 按类目统计 */
  byCategory: Record<SKUCategory, number>;
  /** 按状态统计 */
  byStatus: Record<SKUStatus, number>;
  /** 平均价格 */
  avgPrice: number;
  /** 全部 SKU 总价值（按单次售卖计算） */
  totalValue: number;
  /**
   * 全部 SKU 总收入（Sprint 4 P0-4 新增）
   * 汇总所有 salesRecords 的 revenue
   */
  totalRevenue: number;
  /** 全部 SKU 总利润（Sprint 4 P0-4 新增） */
  totalProfit: number;
  /** 总成交笔数（Sprint 4 P0-4 新增） */
  totalDeals: number;
}

/** SKU Runtime 响应 */
export interface SKUResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}
